import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import store from "../../App/store";
import { getCustomerToken, customerApi } from "../../api/client";
import "./CartLeaveReminder.css";

const REMINDER_MS = 3 * 60 * 1000;
const STORAGE_KEY = "cart_tab_hidden_at";
const TOAST_ID = "cart-return-reminder";

function readHiddenAt() {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY);
    if (v == null) return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function clearHiddenAt() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function cancelAbandonmentEmail() {
  const token = getCustomerToken();
  if (!token) return;
  customerApi("/api/customer/cart-abandonment/cancel", { method: "POST" }).catch(() => {});
}

function registerAbandonmentEmail(cartItems, pathname) {
  const token = getCustomerToken();
  if (!token || !cartItems.length) return;
  const slim = cartItems.map((it) => ({
    productName: it.productName || it.product_name || "Item",
    quantity: it.quantity ?? 1,
  }));
  customerApi("/api/customer/cart-abandonment/register", {
    method: "POST",
    body: JSON.stringify({ cartItems: slim, pathname: pathname || "/" }),
  }).catch(() => {});
}

/**
 * In-tab toast after 3+ min away (all users with items in cart).
 * Logged-in users: also schedule an email if they stay away 3+ min (cancelled when they return, clear cart, checkout, or logout).
 */
export default function CartLeaveReminder() {
  const location = useLocation();
  const cartItemCount = useSelector((s) => s.cart.items.length);
  const pathnameRef = useRef(location.pathname);

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === "/payment/success" || cartItemCount === 0) {
      clearHiddenAt();
      cancelAbandonmentEmail();
    }
  }, [location.pathname, cartItemCount]);

  useEffect(() => {
    const tryShowReminder = () => {
      if (location.pathname === "/payment/success") return;

      const hiddenAt = readHiddenAt();
      if (hiddenAt == null) return;

      const elapsed = Date.now() - hiddenAt;
      if (elapsed < REMINDER_MS) return;

      const items = store.getState().cart?.items ?? [];
      if (!items.length) {
        clearHiddenAt();
        return;
      }

      clearHiddenAt();

      toast.custom(
        (t) => (
          <div
            className={`cartLeaveReminder ${t.visible ? "cartLeaveReminder--visible" : ""}`}
            role="status"
          >
            <p className="cartLeaveReminder__title">You left something behind</p>
            <p className="cartLeaveReminder__text">
              Your cart still has items. Ready to finish your rental checkout?
            </p>
            <div className="cartLeaveReminder__actions">
              <Link to="/cart" className="cartLeaveReminder__cta" onClick={() => toast.dismiss(t.id)}>
                View cart
              </Link>
              <button type="button" className="cartLeaveReminder__dismiss" onClick={() => toast.dismiss(t.id)}>
                Dismiss
              </button>
            </div>
          </div>
        ),
        {
          id: TOAST_ID,
          duration: 12000,
          position: "top-center",
        }
      );
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        try {
          sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
        } catch {
          /* ignore */
        }
        const items = store.getState().cart?.items ?? [];
        const path = pathnameRef.current || "/";
        if (
          getCustomerToken() &&
          items.length > 0 &&
          path !== "/payment/success" &&
          !path.startsWith("/admin")
        ) {
          registerAbandonmentEmail(items, path);
        }
      } else {
        cancelAbandonmentEmail();
        tryShowReminder();
      }
    };

    window.addEventListener("pageshow", tryShowReminder);
    document.addEventListener("visibilitychange", onVisibility);

    tryShowReminder();

    return () => {
      window.removeEventListener("pageshow", tryShowReminder);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [location.pathname]);

  return null;
}
